import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable22 = ({ data }) => {
  const pdfRef = useRef();

  // numeric conversion utility
  const numericData = data.map((d) => ({
    ...d,
    employment: Number(d.employment),
  }));

  // calculations
  const years = numericData.map((d) => d.year);
  const employmentValues = numericData.map((d) => d.employment);

  const overallAverage =
    employmentValues.reduce((s, v) => s + v, 0) / employmentValues.length;

  const max = numericData.reduce((a, b) =>
    a.employment > b.employment ? a : b
  );
  const min = numericData.reduce((a, b) =>
    a.employment < b.employment ? a : b
  );

  const chartData = {
    labels: [...years, "Avg"],
    datasets: [
      {
        label: "Employment",
        data: [...employmentValues, Number(overallAverage.toFixed(2))],
        backgroundColor: [
          ...employmentValues.map(() => "#B4530950"),
          "rgba(255, 99, 132, 0.9)", // Highlight average bar
        ],
        borderColor: [
          ...employmentValues.map(() => "#B45309"),
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "📊 American Indian & Alaska Native Employment (2018-2024)",
        font: { size: 20, weight: "bold" },
      },
    },
    scales: {
      y: {
        title: { display: true, text: "Employment" },
        beginAtZero: true,
      },
      x: {
        title: { display: true, text: "Year" },
      },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      // header branding
      pdf.addImage(utahData, "PNG", 10, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 50, 20);
      pdf.line(10, 32, 200, 32);

      pdf.addImage(img, "PNG", 5, 35, width, height);

      pdf.line(10, 260, 200, 260);

      pdf.addImage(utahData, "PNG", 20, 265, 20, 14);
      pdf.setFontSize(10);
      pdf.text("Built by", 45, 276);
      pdf.addImage(bodex, "PNG", 58, 268, 40, 10);
      pdf.text("using", 105, 276);
      pdf.addImage(kpi, "PNG", 118, 265, 18, 18);

      pdf.text(`Version: 20250916.v3.198`, 150, 276);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 140, 292);
      pdf.text(`Source: ${window.location.href}`, 10, 292);

      pdf.save("EngTable22_Employment.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-xl">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          📍 American Indian & Alaska Native Employment (2018-2024)
        </h2>

        {/* ✔ Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-100 rounded-lg shadow-lg text-center">
            <p className="text-sm text-gray-600">📈 Highest Recorded</p>
            <p className="text-lg font-semibold text-green-700">
              {max.employment} (Year: {max.year})
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-lg shadow-lg text-center">
            <p className="text-sm text-gray-600">📉 Lowest Recorded</p>
            <p className="text-lg font-semibold text-red-700">
              {min.employment} (Year: {min.year})
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-lg shadow-lg text-center">
            <p className="text-sm text-gray-600">🧮 Average Employment</p>
            <p className="text-lg font-semibold text-blue-700">
              {overallAverage.toFixed(2)}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Employment</th>
                  </tr>
                </thead>
                <tbody>
                  {numericData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition">
                      <td className="p-2 font-semibold">{row.year}</td>
                      <td className="p-2">
                        {Number(row.employment).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td className="p-2">Average</td>
                    <td className="p-2">{overallAverage.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable22;
